// src/pages/Customer.js
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Barcode from 'react-barcode';
import StageFrame from '../components/StageFrame';
import FullBleed from '../components/FullBleed';
import Popup from '../components/Popup';
import bg from '../assets/card.png';

import { DESIGN_W, DESIGN_H, MAX_PC_WIDTH, POS, LEVELS, levelIndex } from '../components/card/layout';
import CardTab from '../components/card/CardTab';
import CardHeader from '../components/card/CardHeader';
import CardLabels from '../components/card/CardLabels';
import CardStats from '../components/card/CardStats';
import CardProgress from '../components/card/CardProgress';
import CardInfo from '../components/card/CardInfo';
import CardBarcode from '../components/card/CardBarcode';
import PhoneInput from '../components/PhoneInput';
import { VENUE_MEMBER_CENTER_URL } from '../constants/venueLinks';

axios.defaults.withCredentials = true;

const PROFILE_CACHE_KEY = 'card_profile_cache_v1';
const NIGHT_CACHE_KEY = 'card_night_cache_v1';
const CACHE_TTL_MS = 5 * 60 * 1000;
const CACHE_FALLBACK_TTL_MS = 24 * 60 * 60 * 1000;

function readCacheFrom(storage, key, ttlMs) {
  if (!storage) return null;
  try {
    const raw = storage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const { data, cachedAt } = parsed;
    if (!data || !cachedAt) return null;
    if (Date.now() - Number(cachedAt) > ttlMs) return null;
    return data;
  } catch {
    return null;
  }
}

function readCache(key) {
  return (
    readCacheFrom(window.sessionStorage, key, CACHE_TTL_MS) ||
    readCacheFrom(window.localStorage, key, CACHE_FALLBACK_TTL_MS)
  );
}

function writeCache(key, data) {
  try {
    sessionStorage.setItem(key, JSON.stringify({ data, cachedAt: Date.now() }));
  } catch {}
  try {
    localStorage.setItem(key, JSON.stringify({ data, cachedAt: Date.now() }));
  } catch {}
}

function clearCardCaches() {
  try { sessionStorage.removeItem(PROFILE_CACHE_KEY); } catch {}
  try { sessionStorage.removeItem(NIGHT_CACHE_KEY); } catch {}
  try { localStorage.removeItem(PROFILE_CACHE_KEY); } catch {}
  try { localStorage.removeItem(NIGHT_CACHE_KEY); } catch {}
}

const MENU_ITEMS = [
  { key: 'calendar', label: '活動日曆',     icon: '📆' },
  { key: 'seatBooking', label: '座位預約',  icon: '🪑' },
  { key: 'booking',  label: '我的預約',     icon: '📅' },
  { key: 'coupons',  label: '優惠券',       icon: '🎫' },
  { key: 'settings', label: '設定',         icon: '⚙️' },
  { key: 'benefits', label: '會員權益',     icon: '⭐' },
  { key: 'share',    label: '分享日券',     icon: '🎁' },
];

export default function Customer() {
  const [profile, setProfile] = useState(() => readCache(PROFILE_CACHE_KEY));
  const [night, setNight] = useState(() => {
    const cached = readCache(NIGHT_CACHE_KEY);
    return cached ? { loading: false, ...cached } : { loading: true };
  });
  const [showBarcodePopup, setShowBarcodePopup] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [sharePhone, setSharePhone] = useState('');
  const [shareAmount, setShareAmount] = useState('1');
  const [shareError, setShareError] = useState('');
  const [shareLoading, setShareLoading] = useState(false);
  const [showBookingsModal, setShowBookingsModal] = useState(false);
  const [bookingView, setBookingView] = useState('active');
  const [myBookings, setMyBookings] = useState([]);
  const [myBookingsLoading, setMyBookingsLoading] = useState(false);
  const [myBookingsError, setMyBookingsError] = useState('');

  useEffect(() => {
    const fetchProfile = axios.get('/api/member/profile')
      .then(({ data }) => {
        setProfile(data || {});
        writeCache(PROFILE_CACHE_KEY, data || {});
      })
      .catch((e) => {
        if (e?.response?.status === 401) {
          clearCardCaches();
          setProfile({});
        } else {
          setProfile((prev) => prev || readCache(PROFILE_CACHE_KEY) || {});
        }
      });

    const fetchNight = axios.get('/api/member/night/status')
      .then(({ data }) => {
        setNight({ loading: false, ...data });
        writeCache(NIGHT_CACHE_KEY, data || {});
      })
      .catch(() => {
        setNight((prev) => ({ ...(prev || {}), loading: false }));
      });

    Promise.all([fetchProfile, fetchNight]);
  }, []);

  useEffect(() => {
    if (!showBookingsModal) return;
    let alive = true;
    setMyBookingsLoading(true);
    setMyBookingsError('');
    axios.get('/api/member/my-bookings', { params: { view: bookingView } })
      .then(({ data }) => {
        if (!alive) return;
        setMyBookings(Array.isArray(data?.list) ? data.list : []);
      })
      .catch((e) => {
        if (!alive) return;
        setMyBookingsError(e?.response?.status === 401 ? '請先登入' : '載入失敗');
        setMyBookings([]);
      })
      .finally(() => {
        if (alive) setMyBookingsLoading(false);
      });
    return () => { alive = false; };
  }, [showBookingsModal, bookingView]);

  const handleSharePass = async () => {
    const phoneDigits = (sharePhone || '').trim();
    if (!phoneDigits || !/^\+\d{7,15}$/.test(phoneDigits)) {
      setShareError('請輸入正確的電話號碼');
      return;
    }
    const amount = parseInt(shareAmount);
    if (!amount || amount <= 0) {
      setShareError('請輸入有效的張數');
      return;
    }
    if (amount > (profile?.DAY_PASS || 0)) {
      setShareError('日券不足');
      return;
    }

    setShareError('');
    setShareLoading(true);

    try {
      const { data } = await axios.post('/api/member/share-pass', {
        phone: phoneDigits,
        amount
      });
      setProfile({ ...profile, DAY_PASS: data.dayPass });
      setShowShareModal(false);
      setSharePhone('');
      setShareAmount('1');
      alert(data.message || '分享成功');
    } catch (err) {
      const errorMsg = err.response?.data?.error || '分享失敗';
      setShareError(errorMsg);
    } finally {
      setShareLoading(false);
    }
  };

  const handleMenuClick = (key) => {
    switch (key) {
      case 'share':
        setShowShareModal(true);
        break;
      case 'benefits':
        window.location.href = '/member-benefits';
        break;
      case 'coupons':
        window.location.href = '/coupons';
        break;
      case 'settings':
        window.location.href = '/settings';
        break;
      case 'booking':
        setBookingView('active');
        setShowBookingsModal(true);
        break;
      case 'seatBooking':
        window.location.href = VENUE_MEMBER_CENTER_URL;
        break;
      case 'calendar':
        window.location.href = '/event-calendar';
        break;
      default:
        break;
    }
  };

  return (
    <FullBleed bg={bg}>
      <StageFrame
          designW={DESIGN_W || 1080}
          designH={DESIGN_H || 1920}
          maxPcWidth={MAX_PC_WIDTH || 1080}
          overlay={({ px }) => {
            const p = profile || {};
            return (
              <>
                <CardTab px={px} pos={POS.tab} anchor="tl" level={p?.LEVEL} />
                <CardHeader px={px} profile={p} />
                <CardLabels px={px} />
                <CardStats  px={px} profile={p} />

                <CardProgress
                  px={px}
                  current={(levelIndex(profile?.LEVEL) + 1)}
                  max={LEVELS.length}
                  y={POS.progressRankY}
                  mode="steps"
                  colors={{ done: '#5e3a1a', remain: '#c28b4e' }}
                />

                <CardProgress
                  px={px}
                  current={p?.POINTS ?? 0}
                  max={20}
                  y={POS.progressPointY+70}
                />

                <CardBarcode
                  px={px}
                  value={p?.PHONE}
                  onClick={() => setShowBarcodePopup(true)}
                />
                <CardInfo px={px} profile={p} night={night} />

                {/* 底部功能列（可左右滑） */}
                <div style={{
                  position: 'absolute',
                  left: px(70),
                  right: px(70),
                  top: px(1710),
                  zIndex: 2,
                }}>
                  <div style={{
                    fontSize: px(30),
                    fontWeight: 700,
                    color: '#5e3a1a',
                    marginBottom: px(12),
                    textAlign: 'center',
                    opacity: 0.85,
                  }}>
                    左右滑動查看更多功能
                  </div>
                  <div style={{
                    display: 'flex',
                    gap: px(12),
                    overflowX: 'auto',
                    paddingBottom: px(12),
                    WebkitOverflowScrolling: 'touch',
                    scrollSnapType: 'x mandatory',
                  }}>
                    {MENU_ITEMS.map((item) => (
                      <button
                        key={item.key}
                        onClick={() => handleMenuClick(item.key)}
                        style={{
                          flex: '0 0 auto',
                          minWidth: px(250),
                          height: px(142),
                          border: `${px(2)}px solid #c28b4e`,
                          borderRadius: px(16),
                          background: '#faf5f0',
                          color: '#5e3a1a',
                          fontWeight: 900,
                          fontSize: px(34),
                          letterSpacing: px(1),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: px(8),
                          padding: `0 ${px(16)}px`,
                          boxShadow: `0 ${px(6)}px ${px(18)}px rgba(0,0,0,.08)`,
                          scrollSnapAlign: 'start',
                          cursor: 'pointer',
                        }}
                        onMouseDown={(e) => { e.currentTarget.style.background = '#efe3d6'; }}
                        onMouseUp={(e) => { e.currentTarget.style.background = '#faf5f0'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = '#faf5f0'; }}
                      >
                        <span style={{ fontSize: px(40) }}>{item.icon}</span>
                        <span>{item.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </>
            );
          }}
        />

      {/* 我的預約（座位預約表：LINE／電話串聯） */}
      {showBookingsModal && (
        <Popup onClose={() => setShowBookingsModal(false)} maxWidth="440px" panelBg="#f0dfd4">
          <div style={{ position: 'relative' }}>
            <button
              type="button"
              onClick={() => setShowBookingsModal(false)}
              style={{
                position: 'absolute',
                top: -8,
                right: -8,
                width: 36,
                height: 36,
                border: 'none',
                background: 'none',
                fontSize: 24,
                color: '#5e3a1a',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 0,
                lineHeight: 1,
                minHeight: 'auto',
                borderRadius: '50%',
              }}
            >
              ✕
            </button>
            <div style={{ textAlign: 'center', marginBottom: 12 }}>
              <div style={{
                fontSize: 22,
                fontWeight: 900,
                color: '#5e3a1a',
                letterSpacing: 2,
              }}>
                我的預約
              </div>
              <div style={{ fontSize: 13, color: '#8b7355', marginTop: 6 }}>
                {bookingView === 'cancelled'
                  ? '已取消或已拒絕的座位預約'
                  : '今天起（含）之後的有效座位預約'}
              </div>
            </div>
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              marginBottom: 14,
              gap: 8,
            }}>
              {[
                { key: 'active', label: '有效預約' },
                { key: 'cancelled', label: '已取消' },
              ].map((opt) => {
                const active = bookingView === opt.key;
                return (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setBookingView(opt.key)}
                    style={{
                      padding: '8px 16px',
                      borderRadius: 999,
                      border: active ? 'none' : '1px solid rgba(194,139,78,.45)',
                      background: active ? '#5e3a1a' : 'rgba(255,255,255,.75)',
                      color: active ? '#fff' : '#5e3a1a',
                      fontWeight: 800,
                      fontSize: 14,
                      cursor: 'pointer',
                    }}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
            {myBookingsLoading && (
              <div style={{ padding: 24, textAlign: 'center', color: '#5e3a1a' }}>載入中…</div>
            )}
            {!myBookingsLoading && myBookingsError && (
              <div style={{
                padding: 16,
                background: '#fee',
                color: '#a00',
                borderRadius: 10,
                fontSize: 15,
                textAlign: 'center',
              }}>
                {myBookingsError}
              </div>
            )}
            {!myBookingsLoading && !myBookingsError && myBookings.length === 0 && (
              <div style={{
                padding: 20,
                textAlign: 'center',
                color: '#8b7355',
                fontSize: 16,
                background: 'rgba(194,139,78,.12)',
                borderRadius: 12,
              }}>
                目前沒有{bookingView === 'cancelled' ? '已取消的' : '符合的'}預約紀錄
              </div>
            )}
            {!myBookingsLoading && !myBookingsError && myBookings.length > 0 && (
              <ul style={{
                listStyle: 'none',
                margin: 0,
                padding: 0,
                maxHeight: 'min(52vh, 420px)',
                overflowY: 'auto',
                WebkitOverflowScrolling: 'touch',
              }}>
                {myBookings.map((b) => (
                  <li
                    key={b.id}
                    style={{
                      textAlign: 'left',
                      padding: '14px 12px',
                      marginBottom: 10,
                      background: 'rgba(255,255,255,.88)',
                      borderRadius: 12,
                      border: '1px solid rgba(194,139,78,.35)',
                      fontSize: 15,
                      color: '#3d2a18',
                      lineHeight: 1.55,
                    }}
                  >
                    <div style={{ fontWeight: 800, color: '#5e3a1a', marginBottom: 6 }}>
                      {b.date || '—'}
                      <span style={{ fontWeight: 600, marginLeft: 8 }}>
                        {b.startTime || '—'}{b.endTime ? `–${b.endTime}` : ''}
                      </span>
                    </div>
                    <div>人數：{b.number != null ? b.number : '—'}</div>
                    {b.isNightOwlPlan ? (
                      <div style={{ marginTop: 4 }}>
                        <div style={{ fontWeight: 900, color: '#5e3a1a', letterSpacing: 0.5 }}>
                          夜貓子方案
                        </div>
                        {b.nightOwlPlacementDetail ? (
                          <div style={{ fontWeight: 800, marginTop: 6, fontSize: 17, color: '#3d2a18' }}>
                            {b.nightOwlPlacementDetail}
                          </div>
                        ) : (
                          <div style={{ fontSize: 13, color: '#8b7355', marginTop: 6 }}>
                            實際桌號或區域將於店家擺放預約牌後顯示
                          </div>
                        )}
                      </div>
                    ) : (
                      <div>座位類型：{b.seatTypeLabel || b.seatName || '—'}</div>
                    )}
                    {bookingView === 'cancelled' && b.status ? (
                      <div style={{ marginTop: 6, fontSize: 13, color: '#a0522d', fontWeight: 700 }}>
                        狀態：{String(b.status).toLowerCase() === 'rejected' ? '已拒絕' : '已取消'}
                      </div>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Popup>
      )}

      {/* 條碼彈窗 */}
      {showBarcodePopup && (
        <Popup onClose={() => setShowBarcodePopup(false)} maxWidth="420px" panelBg="#f0dfd4">
          <div style={{ position: 'relative' }}>
            {/* 叉叉關閉按鈕 */}
            <button
              onClick={() => setShowBarcodePopup(false)}
              style={{
                position: 'absolute',
                top: -8,
                right: -8,
                width: 36,
                height: 36,
                border: 'none',
                background: 'none',
                fontSize: 24,
                color: '#5e3a1a',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 0,
                lineHeight: 1,
                minHeight: 'auto',
                borderRadius: '50%',
              }}
            >
              ✕
            </button>

            <div style={{ textAlign: 'center' }}>
              {/* 載具區域 */}
              <div style={{ marginBottom: 48 }}>
                <div style={{
                  fontSize: 24,
                  fontWeight: 900,
                  color: '#5e3a1a',
                  marginBottom: 16,
                  letterSpacing: 3,
                }}>
                  載具
                </div>
                {profile?.CARRIER ? (
                  <div style={{
                    background: 'rgba(255,255,255,.85)',
                    borderRadius: 12,
                    padding: '12px 12px',
                  }}>
                    <div style={{ overflow: 'hidden', display: 'flex', justifyContent: 'center' }}>
                      <Barcode
                        value={String(profile.CARRIER)}
                        displayValue={false}
                        margin={0}
                        width={2}
                        height={60}
                      />
                    </div>
                    <div style={{
                      marginTop: 10,
                      fontSize: 20,
                      fontWeight: 700,
                      color: '#5e3a1a',
                      letterSpacing: 3,
                    }}>
                      {profile.CARRIER}
                    </div>
                  </div>
                ) : (
                  <div style={{
                    color: '#8b7355',
                    fontSize: 18,
                    padding: '20px 16px',
                    background: 'rgba(194,139,78,.12)',
                    borderRadius: 10,
                  }}>
                    尚未設定載具，請至「設定資料與載具」綁定
                  </div>
                )}
              </div>

              <div style={{
                borderTop: '2px dashed #c28b4e',
                margin: '0 -20px',
                opacity: 0.5,
              }} />

              {/* 會員條碼區域 */}
              <div style={{ marginTop: 48 }}>
                <div style={{
                  fontSize: 24,
                  fontWeight: 900,
                  color: '#5e3a1a',
                  marginBottom: 16,
                  letterSpacing: 3,
                }}>
                  會員條碼
                </div>
                {profile?.PHONE ? (
                  <div style={{
                    background: 'rgba(255,255,255,.85)',
                    borderRadius: 12,
                    padding: '12px 12px',
                  }}>
                    <div style={{ overflow: 'hidden', display: 'flex', justifyContent: 'center' }}>
                      <Barcode
                        value={String(profile.PHONE)}
                        displayValue={false}
                        margin={0}
                        width={2}
                        height={60}
                      />
                    </div>
                    <div style={{
                      marginTop: 10,
                      fontSize: 20,
                      fontWeight: 700,
                      color: '#5e3a1a',
                      letterSpacing: 3,
                    }}>
                      {profile.PHONE}
                    </div>
                  </div>
                ) : (
                  <div style={{
                    color: '#8b7355',
                    fontSize: 18,
                    padding: '20px 0',
                  }}>
                    （尚未生成條碼）
                  </div>
                )}
              </div>
            </div>
          </div>
        </Popup>
      )}

      {/* 分享日券彈窗 */}
      {showShareModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }} onClick={() => !shareLoading && setShowShareModal(false)}>
          <div style={{
            background: '#f0dfd4',
            borderRadius: 16,
            padding: 32,
            width: 'calc(100% - 48px)',
            maxWidth: 400,
            boxSizing: 'border-box',
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
            border: '2px solid #5e3a1a',
          }} onClick={(e) => e.stopPropagation()}>
            <h2 style={{ marginTop: 0, marginBottom: 24, color: '#5e3a1a', fontWeight: 900 }}>分享日券</h2>
            
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold', color: '#5e3a1a' }}>
                電話號碼
              </label>
              <PhoneInput
                value={sharePhone}
                onChange={(val) => setSharePhone(val)}
                placeholder="例：912345678"
                disabled={shareLoading}
                selectStyle={{
                  padding: 14,
                  fontSize: 16,
                  border: '2px solid #5e3a1a',
                  borderRight: 'none',
                  borderRadius: '8px 0 0 8px',
                  background: '#fff',
                  minWidth: 100,
                  boxSizing: 'border-box',
                }}
                inputStyle={{
                  flex: 1,
                  minWidth: 0,
                  padding: 14,
                  fontSize: 16,
                  border: '2px solid #5e3a1a',
                  borderRadius: '0 8px 8px 0',
                  background: '#fff',
                  boxSizing: 'border-box',
                }}
              />
              <div style={{ fontSize: 12, color: '#5e3a1a', marginTop: 6, opacity: 0.8 }}>
                台灣請從 9 開始輸入（可輸入 09，系統會自動轉換）
              </div>
            </div>

            <div style={{ marginBottom: 24 }}>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold', color: '#5e3a1a' }}>
                張數
              </label>
              <input
                type="number"
                value={shareAmount}
                onChange={(e) => setShareAmount(e.target.value)}
                min="1"
                max={profile?.DAY_PASS || 0}
                style={{
                  width: '100%',
                  padding: 14,
                  fontSize: 16,
                  border: '2px solid #5e3a1a',
                  borderRadius: 8,
                  boxSizing: 'border-box',
                  background: '#fff',
                }}
                disabled={shareLoading}
              />
              <div style={{ fontSize: 12, color: '#5e3a1a', marginTop: 4, opacity: 0.8 }}>
                您目前有 {profile?.DAY_PASS || 0} 張日券
              </div>
            </div>

            {shareError && (
              <div style={{
                padding: 12,
                background: '#fee',
                color: '#c00',
                borderRadius: 8,
                marginBottom: 16,
                fontSize: 14,
                border: '1px solid #c00',
              }}>
                {shareError}
              </div>
            )}

            <div style={{ display: 'flex', gap: 12 }}>
              <button
                onClick={() => {
                  setShowShareModal(false);
                  setSharePhone('');
                  setShareAmount('1');
                  setShareError('');
                }}
                disabled={shareLoading}
                style={{
                  flex: 1,
                  padding: 14,
                  fontSize: 16,
                  border: '2px solid #5e3a1a',
                  borderRadius: 8,
                  background: '#fff',
                  color: '#5e3a1a',
                  cursor: shareLoading ? 'not-allowed' : 'pointer',
                  fontWeight: 'bold',
                }}
              >
                取消
              </button>
              <button
                onClick={handleSharePass}
                disabled={shareLoading}
                style={{
                  flex: 1,
                  padding: 14,
                  fontSize: 16,
                  border: 'none',
                  borderRadius: 8,
                  background: shareLoading ? '#ccc' : '#5e3a1a',
                  color: '#fff',
                  cursor: shareLoading ? 'not-allowed' : 'pointer',
                  fontWeight: 'bold',
                }}
              >
                {shareLoading ? '處理中...' : '確認分享'}
              </button>
            </div>
          </div>
        </div>
      )}

    </FullBleed>
  );
}
